import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import {
  useResource, PageTitle, Loading, ErrorState, EmptyState, fmtDateTime,
} from "../../components/app/Kit";
import { StatusChip } from "../../components/site/Primitives";
import { VapiPanel, CalendarPanel, TwilioPanel, StripePanel, ResendPanel } from "../../components/app/ProviderPanels";

const CALENDAR_MESSAGES = {
  connected: ["success", "Google Calendar connected successfully."],
  denied: ["warning", "Google Calendar connection was cancelled or denied."],
  invalid_state: ["error", "Calendar connect flow expired. Please try again."],
  token_error: ["error", "Google rejected the token exchange. Check the OAuth client configuration."],
};

const ICONS = {
  database: "Database", phone: "PhoneCall", calendar: "CalendarCheck", message: "MessageSquare",
  whatsapp: "MessagesSquare", card: "CreditCard", mail: "Mail", webhook: "Webhook",
  code: "Code2", server: "Server",
};

export default function Integrations() {
  const { data, state, error, reload, setData } = useResource("/integrations");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    const outcome = new URLSearchParams(window.location.search).get("calendar");
    if (outcome && CALENDAR_MESSAGES[outcome]) {
      const [kind, msg] = CALENDAR_MESSAGES[outcome];
      toast[kind](msg);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const setStatus = (key, status) =>
    setData((list) => list.map((i) => (i.key === key ? { ...i, status, last_checked: new Date().toISOString(), error: null } : i)));

  const act = async (integ, action) => {
    setBusy(`${integ.key}-${action}`);
    try {
      const { data: res } = await api.post(`/integrations/${integ.key}/${action}`, {});
      setData((list) => list.map((i) => (i.key === integ.key ? { ...i, status: res.status, last_checked: new Date().toISOString() } : i)));
      toast[action === "test" ? "info" : "success"](res.message || `${integ.name}: ${res.status.replace(/_/g, " ")}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="integrations-page">
      <PageTitle
        testId="integrations-title"
        title="Integrations"
        sub="Connect the providers you already use. Nothing is marked connected unless credentials are actually present."
      />

      <div
        data-testid="integrations-secrets-note"
        className="mb-4 rounded-lg border border-elv-cyan/20 bg-elv-cyan/[0.05] p-4 text-xs text-elv-dim"
      >
        <span className="text-elv-cyan">Credentials are stored server-side only.</span> API keys and secrets are
        never exposed to the browser. No provider credentials are configured in this environment yet, so every
        integration below reports its real status.
      </div>

      {state === "loading" ? <Loading rows={8} testId="integrations-loading" /> : null}
      {state === "error" ? <ErrorState message={error} onRetry={reload} testId="integrations-error" /> : null}
      {state === "ready" && data.length === 0 ? (
        <EmptyState icon="Plug" title="No integrations available" sub="Integration catalogue could not be loaded." testId="integrations-empty" />
      ) : null}

      {state === "ready" && data.length > 0 ? (
        <div className="grid gap-2 lg:grid-cols-2">
          {data.map((i) => {
            const Icon = Icons[ICONS[i.icon] || "Plug"] || Icons.Plug;
            return (
              <div key={i.key} data-testid={`integration-${i.key}`} className="glass rounded-lg p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md border border-elv-line bg-white/[0.03]">
                      <Icon className="h-4 w-4 text-elv-green" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-elv-text">{i.name}</p>
                      <p className="mt-1 text-[11px] text-elv-dim">{i.description}</p>
                    </div>
                  </div>
                  <StatusChip status={i.status} />
                </div>

                {i.error ? (
                  <p data-testid={`integration-error-${i.key}`} className="mt-3 rounded-md border border-amber-300/25 bg-amber-300/[0.05] p-3 text-[11px] text-amber-200">
                    {i.error}
                  </p>
                ) : null}

                <p className="mt-3 font-mono text-[10px] text-elv-dim">
                  Last checked: {i.last_checked ? fmtDateTime(i.last_checked) : "never"}
                </p>

                {i.key === "vapi" ? <VapiPanel onStatus={(s) => setStatus("vapi", s)} /> : null}
                {i.key === "google_calendar" ? <CalendarPanel onStatus={(s) => setStatus("google_calendar", s)} /> : null}
                {i.key === "twilio" || i.key === "whatsapp" ? <TwilioPanel onStatus={(s) => { setStatus("twilio", s); setStatus("whatsapp", s); }} /> : null}
                {i.key === "stripe" ? <StripePanel /> : null}
                {i.key === "resend" || i.key === "email" ? <ResendPanel /> : null}

                {["vapi", "google_calendar", "twilio", "whatsapp", "stripe", "resend", "email"].includes(i.key) ? null : (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => act(i, "connect")}
                    disabled={busy === `${i.key}-connect`}
                    data-testid={`integration-connect-${i.key}`}
                    className="rounded-md bg-elv-green px-3.5 py-1.5 text-[11px] font-semibold text-[#04120A] disabled:opacity-60"
                  >
                    Connect
                  </button>
                  <button
                    type="button"
                    onClick={() => act(i, "test")}
                    disabled={busy === `${i.key}-test`}
                    data-testid={`integration-test-${i.key}`}
                    className="rounded-md border border-elv-line px-3.5 py-1.5 text-[11px] text-elv-dim hover:text-elv-text disabled:opacity-60"
                  >
                    Test connection
                  </button>
                  <button
                    type="button"
                    onClick={() => act(i, "disconnect")}
                    disabled={busy === `${i.key}-disconnect`}
                    data-testid={`integration-disconnect-${i.key}`}
                    className="rounded-md border border-elv-line px-3.5 py-1.5 text-[11px] text-elv-dim hover:text-red-300 disabled:opacity-60"
                  >
                    Disconnect
                  </button>
                </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
